import React, { useMemo } from 'react';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import {
  getCountryNameByCountryCode,
  showTimeByDateType,
} from '@/pages/intelligence/utils/commonUtils';
import CommonVisualis from '@/pages/intelligence/components/CommonVisualis';
import TipContext from '@home/components/TipContextComponents/TipContext';

const getUniqueSortedDates = (list?: any[] | null): string[] => {
  if (!Array.isArray(list) || list.length === 0) return [];

  const allDates: string[] = [];

  list.forEach((item) => {
    const timeLines = item?.time_line;
    if (Array.isArray(timeLines) && timeLines.length > 0) {
      timeLines.forEach((timeline) => {
        const date = timeline?.time;
        if (!!date) {
          allDates.push(date);
        }
      });
    }
  });

  return [...new Set(allDates)].sort((a, b) => a.localeCompare(b));
};

const RankTrendVisualis = (props: any) => {
  const {
    lineData,
    dateType = 'daily',
    tableSelection,
    trendError,
    loading,
    trendLoading,
    entityType,
    segmentListCur,
    isLineNodata,
    regionOptions,
    appIds,
    segmentValue,
    setSegmentValue,
    handleMetricChangeFun,
    fetchParams,
    firstWeekSinceReleaseDates = [],
  } = props;
  // console.log('RankTrendVisualis', props);

  const { formatMessage, locale } = useIntlExtend();

  // const { regions: marketsList = [] } = fetchParams || {};

  const ifHasGlobal = useMemo(() => {
    return (
      fetchParams?.regions?.length > 0 &&
      fetchParams?.regions?.findIndex((item: any) => item === 'global') > -1
    );
  }, [fetchParams]);

  const noDataTextKeyCur = useMemo(() => {
    if (
      ['daily steam top 100 players', 'steam new and trending', 'steam top wish lists']?.includes(
        segmentValue,
      ) &&
      !ifHasGlobal
    ) {
      return 'dbi_current_storeRank_nodata_global';
    }
    return '';
  }, [ifHasGlobal, segmentValue]);

  const lineDataCur = useMemo(() => {
    if (!Array.isArray(lineData) || lineData?.length < 1) return [];
    if (!Array.isArray(tableSelection) || tableSelection?.length < 1) return [];
    const dataList: any = [];
    tableSelection?.forEach((item: any) => {
      const dataObj = lineData?.find((itemSub: any) => itemSub?.market === item?.market);
      dataList.push(dataObj);
    });
    return dataList;
  }, [tableSelection, lineData]);

  const shouldKeepYAxisWhenNoRank = useMemo(() => {
    if (noDataTextKeyCur !== '') return false;
    return isLineNodata && Array.isArray(tableSelection) && tableSelection.length > 0;
  }, [isLineNodata, noDataTextKeyCur, tableSelection]);

  // 组装rpd折线图数据
  const getHistoryData = (list: any, metricKey = '', dates: string[]) => {
    if ((!Array.isArray(list) || !list.length) && !firstWeekSinceReleaseDates.length) {
      return [];
    }
    if (!Array.isArray(dates) || dates?.length < 1) return [];
    const arr: any = [];

    for (const date of dates) {
      const item = list?.find((item) => item?.time === date);
      const metric = item?.metrics?.find((item) => item?.metric === metricKey);
      const metricValue = metric?.value;

      arr.push({
        metric: metricKey,
        source: '',
        time: showTimeByDateType(date, dateType?.toLocaleLowerCase()),
        value:
          metricValue === '' || metricValue === null || metricValue === undefined
            ? Number.NaN
            : metricValue,
      });
    }

    return arr;
  };

  const getMetricsList = (metricKey: string, list: any, dates: string[]) => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any = [];
    for (const n in list) {
      const objTemp = {
        cover: '',
        id: list[n]?.market,
        name: getCountryNameByCountryCode(regionOptions, list[n]?.market),
        history: getHistoryData(list[n]?.time_line || [], metricKey, dates),
      };
      arr.push(objTemp);
    }
    return arr;
  };

  const lineSeriesData = useMemo(() => {
    const arrGameData: any = {};
    let allDates = getUniqueSortedDates(lineDataCur);
    if (firstWeekSinceReleaseDates.length) {
      allDates = firstWeekSinceReleaseDates;
    }
    for (const n in segmentListCur) {
      arrGameData[segmentListCur[n]?.value] = getMetricsList(segmentListCur[n]?.value, lineDataCur, allDates);
    }
    return arrGameData;
  }, [lineDataCur, segmentListCur, locale, firstWeekSinceReleaseDates]);

  // const statusHeightCur = useMemo(() => {
  //   if (loading || trendLoading) {
  //     return 370;
  //   }
  //   if (noDataTextKeyCur !== '' ? true : isLineNodata) {
  //     return 229;
  //   }
  //   return 370;
  // }, [isLineNodata, trendLoading, loading, noDataTextKeyCur]);

  const currentFetchSourceCur = useMemo(() => {
    return [segmentValue];
  }, [segmentValue]);

  return (
    <CommonVisualis
      noDecimalsLabel
      lineSeriesData={lineSeriesData}
      cardStatus={{
        isLoading: loading || trendLoading,
        isError: trendError,
        isNoData: noDataTextKeyCur !== '' ? true : shouldKeepYAxisWhenNoRank ? false : isLineNodata,
        noDataTextKey: noDataTextKeyCur,
      }}
      entityType={entityType}
      isShowNoDataSegment={true}
      cardTitle={
        appIds?.length > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {formatMessage({ id: `dbi_segment_rank_trend` })}
            <TipContext
              className="tip-text-hd dbi_pc_store_rank_tips"
              tipText={formatMessage({ id: 'dbi_info_rank_trend_mobile_tip_desc' })}
            />
          </div>
        ) : (
          formatMessage({ id: `dbi_segment_rank_trend` })
        )
      }
      segmentList={segmentListCur}
      chartSegmentList={segmentListCur}
      pageId={`detailStorerank-combine-ranktTrend`}
      isHasSegmentChartIcon={true}
      chartType={'line'}
      // statusHeight={statusHeightCur}
      statusHeight={370}
      isStoreRank={true}
      segmentValue={segmentValue}
      setSegmentValue={setSegmentValue}
      handleMetricChangeFun={handleMetricChangeFun}
      currentFetchSource={currentFetchSourceCur}
    />
  );
};
export default RankTrendVisualis;
